/**
 * 首页埋点数据组装工具函数
 * 参考Vue项目的getSDEventParams函数实现
 */

import { ListProduct } from '@/api/types/public';
import { CompleteTrackingParams, ProductListModuleData } from '@/app/(home-page)/types';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { priceInfo } from '@/utils/price';

/**
 * 获取完整的埋点参数
 * @param {ListProduct} item - 商品信息
 * @param {ProductListModuleData} indexData - 模块数据
 * @param {number} moduleIndex - 模块索引
 * @param {number} index - 商品索引
 * @param {number} clickIndex - 子索引
 * @param {string} clickTab - 当前选中的tab
 * @param {any[]} homeListData - 首页数据列表
 * @param {any} currency - 货币信息
 * @param {any} userInfo - 用户信息
 * @returns {CompleteTrackingParams} 完整的埋点参数
 */
export function getSDEventParams(
  item: ListProduct,
  indexData: ProductListModuleData,
  moduleIndex: number,
  index = 0,
  clickIndex = 0,
  clickTab = '1',
  homeListData: any[] = [],
  currency: any = { name: env.NEXT_PUBLIC_DEFAULT_CURRENCY, exchange_rate: 1 },
  userInfo: any = {},
): CompleteTrackingParams {
  try {
    // 获取同类型组件在首页数据中的索引
    const regularListArray = homeListData.filter(
      (hItem) => hItem.component_id === indexData.component_id,
    );
    const regularListIndex = regularListArray.findIndex((rItem) => rItem.id === indexData.id);

    // 确定模块tab名称
    let module_tab_name = 'Eyeglasses';
    if (indexData.btn_display !== 1 && clickTab !== '1') {
      module_tab_name = 'Sunglasses';
    }

    // 获取商品价格信息
    const currentPriceInfo = priceInfo(userInfo, item, currency);
    const basePriceInfo = priceInfo(userInfo, item, {
      name: env.NEXT_PUBLIC_DEFAULT_CURRENCY || currency.name,
    });

    // 组装埋点参数
    const eventParams: CompleteTrackingParams = {
      module_index: moduleIndex,
      module_name: indexData.component_id,
      module_id: String(indexData.blocks_advances_id),
      mkt_index: index,
      mkt_index_sub: clickIndex,
      mkt_jump_type: '2',
      mkt_jump_content: String(item.id || ''),
      module_tab_name,
      coupon_id_list: '',
      coupon_name_list: '',
      shop_sku: String(item.shop_sku || ''),
      business_sku: String((item as any).business_sku || ''),
      product_sku: String((item as any).sku || ''),
      product_id: String(item.id || ''),
      product_name: String(item.short_name || ''),
      group_id: String(indexData.position_jump_product_group?.id || ''),
      group_name: String(indexData.position_jump_product_group?.name || ''),
      module_title: indexData.layout_title,
      product_price: parseFloat(currentPriceInfo.price || '0'),
      current_product_price: parseFloat(currentPriceInfo.sell_price || '0'),
      base_current_product_price: parseFloat(basePriceInfo.sell_price || '0'),
      currency: currency.name,
      fine_ranking_type: (item as any).fine_ranking_type,
      recall_group_type: (item as any).recall_group_type,
      block_flags: `home@horizontal_item_${regularListIndex}@`,
      block_flags_ext: JSON.stringify({
        module_index: moduleIndex,
        module_name: indexData.component_id,
        module_title: indexData.layout_title,
        module_tab_name,
      }),
      dollar_exchange_rate: parseFloat(currency.exchange_rate || '1'),
    };

    return eventParams;
  } catch (error) {
    logger.error('Error generating tracking params:', error);

    // 返回默认的埋点参数
    return {
      module_index: moduleIndex,
      module_name: indexData.component_id,
      module_id: String(indexData.blocks_advances_id),
      mkt_index: index,
      mkt_index_sub: clickIndex,
      mkt_jump_type: '2',
      mkt_jump_content: String(item.id || ''),
      module_tab_name: 'Eyeglasses',
      coupon_id_list: '',
      coupon_name_list: '',
      shop_sku: String(item.shop_sku || ''),
      business_sku: String((item as any).business_sku || ''),
      product_sku: String((item as any).sku || ''),
      product_id: String(item.id || ''),
      product_name: String(item.short_name || ''),
      group_id: String(indexData.position_jump_product_group?.id || ''),
      group_name: String(indexData.position_jump_product_group?.name || ''),
      module_title: indexData.layout_title,
      product_price: 0,
      current_product_price: 0,
      base_current_product_price: 0,
      currency: currency.name || env.NEXT_PUBLIC_DEFAULT_CURRENCY,
      fine_ranking_type: '',
      recall_group_type: '',
      block_flags: `home@horizontal_item_${0}@`,
      block_flags_ext: JSON.stringify({
        module_index: moduleIndex,
        module_name: indexData.component_id,
        module_title: indexData.layout_title,
        module_tab_name: 'Eyeglasses',
      }),
      dollar_exchange_rate: 1,
    };
  }
}

/**
 * 设置block_flags相关标识
 * @param {Object} params - 包含block_flags相关信息的参数对象
 */
export function setBlockFlags(params: {
  block_flags?: string;
  block_flags_ext?: string;
  fine_ranking_type?: string;
  recall_group_type?: string;
}) {
  try {
    // 这里可以根据需要设置全局的block_flags信息
    // 比如存储到sessionStorage或者全局状态中
    if (typeof window !== 'undefined') {
      const blockFlagsData = {
        block_flags: params.block_flags || '',
        block_flags_ext: params.block_flags_ext || '',
        fine_ranking_type: params.fine_ranking_type || '',
        recall_group_type: params.recall_group_type || '',
      };
      sessionStorage.setItem('block_flags_data', JSON.stringify(blockFlagsData));
    }
  } catch (error) {
    logger.error('Error setting block flags:', error);
  }
}

/**
 * 获取block_flags相关标识
 * @returns {Object} block_flags相关信息
 */
export function getBlockFlags() {
  try {
    if (typeof window !== 'undefined') {
      const blockFlagsData = sessionStorage.getItem('block_flags_data');
      if (blockFlagsData) {
        return JSON.parse(blockFlagsData);
      }
    }
  } catch (error) {
    logger.error('Error getting block flags:', error);
  }
  return {
    block_flags: '',
    block_flags_ext: '',
    fine_ranking_type: '',
    recall_group_type: '',
  };
}
